import React, { useState } from 'react';
import welcomeToFliqueImage from '../assets/tutorial/welcome-to-flique.png';
import addingRankingFilmsImage from '../assets/tutorial/adding-ranking-films.png';
import yourFeedImage from '../assets/tutorial/your-feed.png';
import manageYourProfileImage from '../assets/tutorial/manage-your-profile.png';
import settingsImage from '../assets/tutorial/settings.png';
import forYouImage from '../assets/tutorial/for-you.png';

const TUTORIAL_SLIDES = [
  {
    id: 'welcome',
    title: 'Welcome to Flique',
    image: welcomeToFliqueImage,
    text: [
      <>
        <strong>Flique</strong> is your movie log: track what you watch, rank films
        by how you felt, and discover titles through social activity.
      </>,
      <>
        Use this quick walkthrough to learn the six core screens you will use
        most.
      </>,
    ],
  },
  {
    id: 'add-rank',
    title: 'Adding & Ranking Films',
    image: addingRankingFilmsImage,
    text: [
      <>
        Search for a title, open movie details, then set the watched date before
        ranking.
      </>,
      <>
        Choose <strong>Liked</strong> or <strong>Disliked</strong>, then rank the
        movie against others in that same bucket. You can also save titles to your{' '}
        <strong>watchlist</strong>.
      </>,
    ],
  },
  {
    id: 'feed',
    title: 'Your Feed',
    image: yourFeedImage,
    text: [
      <>
        Your <strong>feed</strong> shows recent activity from you and your friends,
        including notes, likes, comments, and ratings.
      </>,
      <>
        Tap a movie to open details, add it to your <strong>watchlist</strong>, or
        rank it from there.
      </>,
    ],
  },
  {
    id: 'for-you',
    title: 'For You Recommendations',
    image: forYouImage,
    text: [
      <>
        The <strong>For You</strong> tab personalizes picks based on your ranked
        movies and what you liked.
      </>,
      <>
        Refresh the list when you want new ideas, then save promising titles to your{' '}
        <strong>watchlist</strong> or open details to rank from there.
      </>,
    ],
  },
  {
    id: 'profile',
    title: 'Manage Your Profile',
    image: manageYourProfileImage,
    text: [
      <>
        Use tabs to switch between <strong>Favorites</strong>,{' '}
        <strong>Recently watched</strong>, and <strong>Watchlist</strong>.
      </>,
      <>
        From your profile you can remove entries, rank watchlist movies, or re-rank
        movies you have already scored.
      </>,
    ],
  },
  {
    id: 'settings',
    title: 'Settings',
    image: settingsImage,
    text: [
      <>
        Open <strong>Settings</strong> from the top-right gear to update your
        username, avatar, and visibility.
      </>,
      <>
        Visibility controls who can find and view your account (Private, Friends
        only, or Public). Have fun logging, ranking, and sharing what you watch!
      </>,
    ],
  },
];

export default function TutorialModal({ open, onClose }) {
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const totalSlides = TUTORIAL_SLIDES.length;

  const currentSlide = TUTORIAL_SLIDES[activeSlideIndex];

  React.useEffect(() => {
    if (open) {
      setActiveSlideIndex(0);
    }
  }, [open]);

  if (!open) return null;

  const onPrev = () => setActiveSlideIndex((idx) => Math.max(0, idx - 1));
  const onNext = () => {
    if (activeSlideIndex >= totalSlides - 1) {
      onClose?.();
      return;
    }
    setActiveSlideIndex((idx) => idx + 1);
  };

  return (
    <div
      className="flique-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="flique-tutorial-title"
      onClick={onClose}
    >
      <div
        className="flique-modal card flique-card flique-tutorial-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-body flique-tutorial-modal-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="small text-muted">
              Step {activeSlideIndex + 1} of {totalSlides}
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-light flique-modal-close"
              aria-label="Close tutorial"
              onClick={onClose}
            />
          </div>

          <h2 id="flique-tutorial-title" className="h5 mb-3">
            {currentSlide.title}
          </h2>

          <img
            src={currentSlide.image}
            alt={`${currentSlide.title} placeholder screenshot`}
            className="flique-tutorial-image mb-3"
          />

          <div className="flique-tutorial-copy">
            {currentSlide.text.map((line, idx) => (
              <p key={`${currentSlide.id}-${idx}`}>{line}</p>
            ))}
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3">
            <button
              type="button"
              className="btn btn-flique-secondary"
              onClick={onPrev}
              disabled={activeSlideIndex === 0}
            >
              Back
            </button>
            <button type="button" className="btn btn-flique flique-tutorial-next-btn" onClick={onNext}>
              {activeSlideIndex === totalSlides - 1 ? 'Done' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
